import { Modal } from "antd";
import React, { useEffect, useState } from "react";
import useRequest from "../../../components/hooks/use-request";
import defaultPhoto from "../../../assets/default-avatar.svg";
import { CircleLoader } from "react-spinners";
import { showToast } from "../../../components/toast";

interface DetailsProps {
  agent: {
    id: string;
    fullname: string;
    email: string;
    status: string;
    created_at: string;
    profilePhoto: string;
    phone: string;
  };
}

interface AgentProfileProps {
  visible: boolean;
  handleClose: () => void;
  id: string;
  agentId?: string;
}

const AgentProfile: React.FC<AgentProfileProps> = ({
  visible,
  handleClose,
  id,
  agentId,
}: any) => {
  const Details = ({ title, value }: any) => {
    return (
      <div className="flex gap-3 text-xs font-semibold">
        <p>{title}</p>
        <p className="text-left capitalize">{value}</p>
      </div>
    );
  };

  const [agent, setAgent] = useState<DetailsProps>();
  const { makeRequest: getRequestById } = useRequest(
    `/customer-support/agents/${id}`,
    "GET"
  );

  // Suspend/delete target the user record via its _id
  const targetId = agentId || id;
  const { makeRequest: suspendAgent, loading: suspendLoading } = useRequest(
    `/users/${targetId}/suspend`,
    "PUT"
  );
  const { makeRequest: deleteAgent, loading: deleteLoading } = useRequest(
    `/users/${targetId}/block`,
    "DELETE"
  );

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getRequestById(undefined, { id });
      setAgent(response?.data || null);
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const isSuspended = agent?.agent?.status === "suspended";

  const handleSuspend = async () => {
    const [response, statusCode] = await suspendAgent();
    if (statusCode >= 200 && statusCode < 300) {
      showToast(
        response?.message ||
          `Agent ${isSuspended ? "unsuspended" : "suspended"} successfully`,
        true,
        { position: "top-center" }
      );
      setTimeout(() => window.location.reload(), 1500);
    } else {
      showToast(
        response?.message ||
          `Unable to ${isSuspended ? "unsuspend" : "suspend"} agent`,
        false,
        { position: "top-center" }
      );
    }
  };

  const handleDelete = async () => {
    const [response, statusCode] = await deleteAgent();
    if (statusCode >= 200 && statusCode < 300) {
      showToast(response?.message || "Agent removed successfully", true, {
        position: "top-center",
      });
      setTimeout(() => window.location.reload(), 1500);
    } else {
      showToast(response?.message || "Unable to remove agent", false, {
        position: "top-center",
      });
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      width={500}
      closable={true}
      centered
      footer={null}
      className="items-center"
      wrapClassName="bg-[#001a3b3e]"
    >
      <div className="">
        <p className="text-[16px] font-bold">Details</p>
        <section className="flex items-center gap-4 mt-8">
          <img
            src={agent?.agent?.profilePhoto || defaultPhoto}
            className="w-[70px] h-[70px] rounded-full object-cover"
          />
          <div>
            <p className="font-semibold capitalize">{agent?.agent?.fullname}</p>
            <p className="text-sm font-normal">{agent?.agent?.email} </p>
          </div>
        </section>
        <div className="mt-4 bg-gray-50 border p-4 rounded-2xl space-y-2">
          <Details title={"Name"} value={agent?.agent?.fullname || "N/A"} />
          <Details
            title={"Email address:"}
            value={agent?.agent?.email || "N/A"}
          />
          <Details title={"Phone:"} value={agent?.agent?.phone || "N/A"} />
        </div>
        <div className="flex items-center gap-4 mt-4">
          <button
            onClick={handleSuspend}
            disabled={suspendLoading}
            className="bg-[#0979A1] text-white text-xs px-3 py-3 rounded-md font-semibold flex items-center justify-center min-w-[110px] disabled:opacity-60"
          >
            {suspendLoading ? (
              <CircleLoader color="#ffffff" loading={suspendLoading} size={16} />
            ) : isSuspended ? (
              "Unsuspend agent"
            ) : (
              "Suspend agent"
            )}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteLoading}
            className="border border-[#0979A1] text-[#0979A1] text-xs px-3 py-3 rounded-md font-semibold flex items-center justify-center min-w-[110px] disabled:opacity-60"
          >
            {deleteLoading ? (
              <CircleLoader color="#0979A1" loading={deleteLoading} size={16} />
            ) : (
              "Remove agent"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentProfile;
